import { IconRobot } from "@tabler/icons-react";
import { observer } from "mobx-react";
import { useState } from "react";
import { toast } from "sonner";
import { InlineBanner } from "@/components/shared/inline-banner";
import { RevealOnMount } from "@/components/shared/reveal-on-mount";
import { SettingsPageShell } from "@/components/shared/settings-page-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Panel } from "@/components/ui/panel";
import { Skeleton } from "@/components/ui/skeleton";
import { isStaleScreenRefusal, useMyTeammates } from "@/hooks/use-teammate-settings";
import { useTranslation } from "@/i18n";
import { useStore } from "@/stores/store-context";
import type { TMemberTeammate, TMemberTeammateState } from "@/types/teammates";
import { memberTeammateAction } from "./member-teammate-state";
import { TeammateConsentDialog } from "./teammate-consent-dialog";
import { TeammatePermissionsDialog } from "./teammate-permissions";
import { TeammateRow } from "./teammate-row";

/**
 * Profile → Digital Teammates (NG-2622, design F1/F5): the teammates that may
 * act in the member's name in the current workspace, and their own switch for
 * each. Turning one on asks for consent to its current version first; turning
 * it off pauses their own teammate only.
 */
export const ProfileDigitalTeammatesPage = observer(function ProfileDigitalTeammatesPage() {
  const { t } = useTranslation();
  const { workspace } = useStore();
  const workspaceSlug = workspace.currentWorkspace?.slug;
  // A deep link (`/profile/digital-teammates`) opens this before it has navigated
  // into a workspace, and before the workspace list has even been asked for, so
  // "no current workspace" is usually "not there yet". Only someone whose list
  // has loaded empty is told to open one; everyone else loads.
  const noWorkspaceAtAll = workspace.hasLoaded && workspace.workspaces.length === 0;

  return (
    <SettingsPageShell
      title={t("profile.digital_teammates.title")}
      description={t("profile.digital_teammates.description")}
    >
      {workspaceSlug ? (
        <MemberTeammatesList key={workspaceSlug} workspaceSlug={workspaceSlug} />
      ) : noWorkspaceAtAll ? (
        <TeammatesEmpty description={t("profile.digital_teammates.no_workspace")} />
      ) : (
        <TeammatesLoading />
      )}
    </SettingsPageShell>
  );
});

const TeammatesEmpty = observer(function TeammatesEmpty({ description }: { description: string }) {
  const { t } = useTranslation();
  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <IconRobot />
        </EmptyMedia>
        <EmptyTitle>{t("profile.digital_teammates.empty_title")}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
});

const MemberTeammatesList = observer(function MemberTeammatesList({
  workspaceSlug,
}: {
  workspaceSlug: string;
}) {
  const { t } = useTranslation();
  const teammates = useMyTeammates(workspaceSlug);
  const { screen } = teammates;
  const [consenting, setConsenting] = useState<TMemberTeammate | null>(null);
  const [saving, setSaving] = useState(false);

  async function confirmConsent() {
    if (!consenting) return;
    const teammate = consenting;
    setSaving(true);
    try {
      await teammates.turnOn(teammate);
      setConsenting(null);
    } catch (error) {
      console.error("[teammates] couldn't turn a teammate on", {
        workspaceSlug,
        teammateSlug: teammate.slug,
        consentVersion: teammate.consent_version,
        error,
      });
      if (isStaleScreenRefusal(error)) {
        // The screen is being re-read; what they consented to is not what runs now.
        setConsenting(null);
        toast.error(t("profile.digital_teammates.version_changed", { name: teammate.name }));
      } else {
        toast.error(t("profile.digital_teammates.save_error", { name: teammate.name }));
      }
    } finally {
      setSaving(false);
    }
  }

  if (!screen && !teammates.failed) {
    return <TeammatesLoading />;
  }

  if (!screen) {
    return (
      <Panel className="space-y-4">
        <InlineBanner tone="destructive" title={t("profile.digital_teammates.load_error")} />
        <Button type="button" variant="outline" onClick={teammates.reload}>
          {t("common.retry")}
        </Button>
      </Panel>
    );
  }

  if (!screen.ai_features_enabled) {
    return <TeammatesEmpty description={t("profile.digital_teammates.ai_off")} />;
  }

  if (screen.teammates.length === 0) {
    return <TeammatesEmpty description={t("profile.digital_teammates.empty_description")} />;
  }

  return (
    <RevealOnMount className="space-y-4">
      {screen.teammates.map((teammate) => (
        <MemberTeammateRow
          key={teammate.slug}
          teammate={teammate}
          onTurnOn={() => setConsenting(teammate)}
          turnOff={teammates.turnOff}
        />
      ))}
      <TeammateConsentDialog
        teammate={consenting}
        saving={saving}
        onCancel={() => setConsenting(null)}
        onConfirm={() => void confirmConsent()}
      />
    </RevealOnMount>
  );
});

/** The state's badge. A switch over the union, so a state added to the
 *  contract is a type error here rather than a badge that never renders. */
const StateBadge = observer(function StateBadge({ state }: { state: TMemberTeammateState }) {
  const { t } = useTranslation();
  switch (state) {
    case "active":
      return <Badge variant="status-success">{t("profile.digital_teammates.state.active")}</Badge>;
    case "not_activated":
      return (
        <Badge variant="status-neutral">{t("profile.digital_teammates.state.not_activated")}</Badge>
      );
    case "paused":
      return <Badge variant="status-neutral">{t("profile.digital_teammates.state.paused")}</Badge>;
    case "needs_consent":
      return (
        <Badge variant="status-warning">{t("profile.digital_teammates.state.needs_consent")}</Badge>
      );
    case "waiting_for_admin":
      return (
        <Badge variant="status-warning">
          {t("profile.digital_teammates.state.waiting_for_admin")}
        </Badge>
      );
  }
});

const MemberTeammateRow = observer(function MemberTeammateRow({
  teammate,
  onTurnOn,
  turnOff,
}: {
  teammate: TMemberTeammate;
  onTurnOn: () => void;
  turnOff: (teammateSlug: string) => Promise<TMemberTeammate>;
}) {
  const { t } = useTranslation();
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [turningOff, setTurningOff] = useState(false);
  const action = memberTeammateAction(teammate.state);

  async function onTurnOff() {
    setTurningOff(true);
    try {
      await turnOff(teammate.slug);
    } catch (error) {
      // The row is already back on; say so.
      console.error("[teammates] couldn't turn a teammate off", {
        teammateSlug: teammate.slug,
        error,
      });
      toast.error(t("profile.digital_teammates.save_error", { name: teammate.name }));
    } finally {
      setTurningOff(false);
    }
  }

  return (
    <>
      <TeammateRow
        name={teammate.name}
        summary={teammate.summary}
        busy={turningOff}
        status={<StateBadge state={teammate.state} />}
        onViewPermissions={() => setPermissionsOpen(true)}
        control={
          action === "turn_on" ? (
            <Button type="button" size="sm" onClick={onTurnOn}>
              {t("profile.digital_teammates.turn_on")}
            </Button>
          ) : action === "turn_off" ? (
            <Button type="button" size="sm" variant="outline" onClick={() => void onTurnOff()}>
              {t("profile.digital_teammates.turn_off")}
            </Button>
          ) : null
        }
        notice={
          action === "none" ? (
            <InlineBanner tone="neutral" title={t("profile.digital_teammates.waiting_note")} />
          ) : null
        }
      />
      <TeammatePermissionsDialog
        open={permissionsOpen}
        onOpenChange={setPermissionsOpen}
        name={teammate.name}
        scopes={teammate.scopes}
      />
    </>
  );
});

const TeammatesLoading = observer(function TeammatesLoading() {
  return (
    <Panel className="space-y-3" aria-busy="true">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-4 w-80" />
    </Panel>
  );
});
